import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import OverlayPage from 'components/dine-in/OverlayPage'
import SearchCategoriesAndProducts from 'components/SearchCategoriesAndProducts'
import { useTranslation } from 'react-i18next'
import { ProductListSectionWrapper } from './ProductListStyled'
import { MenuCategoryType } from 'types'
import { ProductType } from 'components/buffet/ProductCard'
import { type BuffetProductListQuery, ProductServiceEnum } from 'graphQL/dinein/generate/operations'
import { filterSpecialCharacters } from 'helpers/string'
import CartIcon from './CartIcon'
import useLanguage from 'hooks/useLanguage'
import { getResizableURL } from 'helpers/image'
import BuffetProductList from 'components/buffet/BuffetProducList'
import { type Vendor } from 'components/ProductCardV2'

type BuffetProductListType = BuffetProductListQuery['listBuffetPackageProducts']['products']

interface OverlaySearchProductsProps {
  isOpen: boolean
  onBack: () => void
  menuCategories: MenuCategoryType[]
  vendor: Vendor
  onClickCategoryMenu: (category: MenuCategoryType) => void
  membershipProgram: any
  buffetProducts: BuffetProductListType
  onAddToCart: (id: number) => void
  onClickProduct: (id: number) => void
  buffetPackageID: number
}

const OverlaySearchProducts = ({
  isOpen,
  onBack,
  menuCategories,
  vendor,
  onClickCategoryMenu,
  membershipProgram,
  buffetProducts,
  onAddToCart,
  onClickProduct,
  buffetPackageID,
}: OverlaySearchProductsProps) => {
  const { t } = useTranslation()
  const { getPropertyByLang } = useLanguage()
  const [keyword, setKeyword] = useState('')

  const { includedProducts, excludedProducts, aLaCarteProducts } = useMemo(() => {
    if (keyword) {
      const includedProducts: BuffetProductListType = []
      const excludedProducts: ProductType[] = []
      const aLaCarteProducts: BuffetProductListType = []
      const _keyword = filterSpecialCharacters(keyword).toLowerCase()
      buffetProducts.forEach((product) => {
        const inPackage = product.buffetPackageProducts.find((p) => p.buffetPackageID === buffetPackageID)
        const isMatching =
          product.nameEn.toLowerCase().includes(_keyword) || product.nameTh.toLowerCase().includes(_keyword)
        if (isMatching && product.active) {
          if (inPackage) {
            includedProducts.push(product)
          } else if (product.availableServices.includes(ProductServiceEnum.BuffetALaCarte)) {
            aLaCarteProducts.push(product)
          } else {
            const packageName = getPropertyByLang({
              th: product.buffetPackageProducts[0]?.buffetPackage.nameTh,
              en: product.buffetPackageProducts[0]?.buffetPackage.nameTh,
            })
            const image = {
              image1x: getResizableURL(product.images, { width: '250', height: '250', formatType: '_fmt-jpg' }),
              image2x: getResizableURL(product.images, { width: '330', height: '330', formatType: '_fmt-jpg' }),
              image3x: getResizableURL(product.images, { width: '375', height: '375', formatType: '_fmt-jpg' }),
            }
            excludedProducts.push({
              id: product.id,
              name: getPropertyByLang({ th: product.nameTh, en: product.nameEn }),
              label: t('buffet.productListPage.excluded'),
              description: packageName,
              image,
            })
          }
        }
      })
      return { includedProducts, aLaCarteProducts, excludedProducts }
    } else {
      return {
        includedProducts: [],
        aLaCarteProducts: [],
        excludedProducts: [],
      }
    }
  }, [keyword, buffetPackageID, buffetProducts])

  return (
    <OverlayPage isShow={isOpen} animation>
      <SearchCategoriesAndProducts
        show={isOpen}
        menuCategories={menuCategories}
        listProducts={[]}
        onClickCategoryMenu={(data) => {
          setKeyword('')
          onClickCategoryMenu(data)
        }}
        onClickProduct={() => {}}
        onBack={() => {
          setKeyword('')
          onBack()
        }}
        vendor={vendor}
        membershipProgram={membershipProgram}
        handleAddToCart={() => {}}
        onKeywordChange={(keyword) => setKeyword(keyword)}
      >
        <ProductListSectionWrapper id="product-search-result">
          <BuffetProductList
            includedProducts={includedProducts}
            aLaCarteProducts={aLaCarteProducts}
            excludedProducts={excludedProducts}
            onClickProduct={onClickProduct}
            onAddProductToCart={onAddToCart}
            isShowOnlyExcludedProducts={false}
            vendor={vendor}
            membershipProgram={membershipProgram}
          />
        </ProductListSectionWrapper>
      </SearchCategoriesAndProducts>
      <CartIconWrapper id="search-product-cart-quantity">
        <CartIcon />
      </CartIconWrapper>
    </OverlayPage>
  )
}

export default OverlaySearchProducts

const CartIconWrapper = styled.div`
  z-index: 99;
  position: fixed;
  bottom: 15px;
  right: 15px;
`
